import React, {
	ComponentType,
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import store from 'store2';

import Modal, { ModalProps } from '@skippay/core/components/Modal/Modal';
import useLocation from '@skippay/core/hooks/useLocation';
import { isFirstAccess } from '@skippay/core/utils/firstAccess';

type OpenModalOptions = {
	id: string;
	Component: ComponentType<ModalProps>;
	props?: ModalProps;
	priority?: number;
	expires?: number;
	repeatable?: boolean;
	delayedLaunch?: boolean;
};

export type ModalItem = Required<Pick<OpenModalOptions, 'id' | 'Component'>> &
	Partial<Pick<OpenModalOptions, 'priority' | 'expires' | 'repeatable' | 'delayedLaunch'>> & {
		props: ModalProps;
	};

interface ModalContextType {
	openModal: (options: OpenModalOptions) => void;
	closeModal: (id?: string) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
	const ctx = useContext(ModalContext);
	if (!ctx) throw new Error('useModal must be used within a ModalProvider');
	return ctx;
};

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [modals, setModals] = useState<ModalItem[]>([]);
	const [isModalOpened, setIsModalOpened] = useState(false);
	const location = useLocation();

	const openModal = useCallback((options: OpenModalOptions) => {
		const { id, Component, props = {}, priority = 0, expires, repeatable, delayedLaunch } = options;

		if (id && !repeatable) {
			const viewed = store.local.get(id);
			if (viewed && !expires) return;
			if (viewed?.expires && (!expires || viewed.expires > Date.now())) return;
		}

		setModals((current) => [
			...current,
			{ id, Component, props, priority, expires, repeatable, delayedLaunch },
		]);
	}, []);

	const eligibleModals = useMemo(() => {
		const allowDelayed = !isFirstAccess();
		return modals.filter((modal) => !modal.delayedLaunch || allowDelayed);
	}, [modals]);

	const highestPriorityModal = useMemo(() => {
		if (!eligibleModals.length) return null;
		return eligibleModals.reduce(
			(highest, modal) => ((modal.priority || 0) > (highest.priority || 0) ? modal : highest),
			eligibleModals[0]
		);
	}, [eligibleModals]);

	const closeModal = useCallback(
		(id?: string) => {
			if (!highestPriorityModal) return;
			if (!id || highestPriorityModal.id === id) {
				store.local.set(highestPriorityModal.id, {
					value: true,
					expires: highestPriorityModal.expires
						? Date.now() + highestPriorityModal.expires
						: undefined,
				});
				setModals([]);
			}
		},
		[highestPriorityModal]
	);

	useEffect(() => {
		setIsModalOpened(false);
		const timer = setTimeout(() => setIsModalOpened(true), 2000);
		return () => clearTimeout(timer);
	}, [location.location.pathname]);

	return (
		<ModalContext.Provider value={{ openModal, closeModal }}>
			{children}
			{highestPriorityModal && (
				<Modal
					{...highestPriorityModal.props}
					isOpen={isModalOpened}
					onClose={() => {
						highestPriorityModal.props.onClose?.();
						closeModal();
					}}
				>
					{React.createElement(highestPriorityModal.Component, {
						...highestPriorityModal.props,
						onClose: () => {
							highestPriorityModal.props.onClose?.();
							closeModal();
						},
					})}
				</Modal>
			)}
		</ModalContext.Provider>
	);
};
